import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Grid,
  Typography,
  Box,
} from '@material-ui/core';

import { selectItem, saveItem, clearSaveStatus } from './itemSlice';
import { enqueueSnackbar } from '../snackbar/snackbarSlice';
import { selectGlobals } from '../app/appSlice';

import FilePicker from '../common/filePicker';
import {
  BooleanField,
  TextField,
} from '../common/fields';
import ActionBar from './ActionBar';
import DeleteList from '../common/deleteList';
import MainImage from '../itemFamily/MainImage';

import { defaultStudioFolderPC24 } from '../../constants';

const defaultStudioFolder = defaultStudioFolderPC24;

const ItemPagePC24 = (props) => {
  const { itemOwnerId, itemId } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { canEdit: userAllowedToEdit } = props;
  const { editableFields, item, saveStatus, saveError } = useSelector(selectItem);
  const { attributes } = useSelector(selectGlobals);
  const [ itemData, setItemData ] = useState(item);
  const [ hasUnsavedChanges, setHasUnsavedChanges ] = useState(false);

  useEffect(() => {
    // Show feedback on save status
    if(saveStatus === 'failed') {
      dispatch(enqueueSnackbar({
        message: t(`errors:${saveError.message}`),
        options: {
          variant: 'error'
        }
      }));
      dispatch(clearSaveStatus());
    } else if(saveStatus === 'succeeded') {
      dispatch(enqueueSnackbar({
        message: t('common:savedSuccessfully'),
        options: {
          variant: 'success'
        }
      }));
      dispatch(clearSaveStatus());
      setHasUnsavedChanges(false);
    }
  }, [saveStatus]);

  const fieldIsDisabled = (field) => {
    if(userAllowedToEdit && editableFields.includes(field)) {
      return false;
    }

    return true;
  }

  const handleChange = (field, value) => {
    setItemData({
      ...itemData,
      [field]: value
    });
    setHasUnsavedChanges(true);
  }

  const handleFieldChange = event => {
    handleChange(event.target.name, event.target.value);
  }

  const filterCountryExclusions = () => {
    return itemData.itemFamilyMarkets.map(country => ({value: country, excluded: itemData.excludeMarkets.includes(country), label: t(`countries:${country}`)}));
  }

  const filterAttributeExclusions = (familyField, itemField, globalField) => {
    const itemValues = itemData[familyField];

    return itemValues.map(attribute => {
      let label = attribute;

      if(attributes[globalField]) {
        const row = attributes[globalField].find(item => item.id === attribute);
        if(row) {
          label = row.global_name;
        }
      }

      return {value: attribute, label: label, excluded: itemData[itemField].includes(attribute)}
    });
  };

  const handleDeleteListItem = (field, item) => {
    const newState = item.excluded ? itemData[field].filter(i => i !== item.value) : itemData[field].concat(item.value) ;
    handleChange(field, newState);
  }

  const cancelChanges = () => {
    setItemData(item);
    setHasUnsavedChanges(false);
  }

  const saveChanges = () => {
    // Save to server
    dispatch(saveItem({itemOwnerId, itemId, itemData}));
  }

  // Combine multiple fields
  let loadIndex = '';
  let loadIndex2 = '';
  if(itemData.loadIndexCode) {
    loadIndex = itemData.loadIndexCode;
  }
  if(itemData.loadIndexDualCode) {
    loadIndex += `/${itemData.loadIndexDualCode}`;
  }
  if(itemData.speedIndexCode) {
    loadIndex += itemData.speedIndexCode;
  }

  if(itemData.loadIndex2Code) {
    loadIndex2 = itemData.loadIndex2Code;
  }
  if(itemData.loadIndex2DualCode) {
    loadIndex2 += `/${itemData.loadIndex2DualCode}`;
  }
  if(itemData.speedIndex2Code) {
    loadIndex2 += itemData.speedIndex2Code;
  }

  // Trucks and buses (TBR) don't need all fields
  // Determine type from item line
  const isTBR = itemData?.itemLineCode === 'IL-PC24T-001';

  return (
    <>
      <Grid container spacing={7}>
        <Grid item xs={12} md={8}>
          <Box mb={2}>
            <Typography variant="subtitle1">{t('item:itemName')}</Typography>
            <Typography variant="h4" component="h1">{item.itemName}</Typography>
          </Box>

          <Box mb={3}>
            <Typography variant="subtitle1">{t('item:itemCode')}</Typography>
            <Typography variant="h5">{item.itemCode}</Typography>
          </Box>

          <Box mb={4}>
            <Typography variant="overline" display="block">{t('item:lastChanged')}</Typography>
            <Typography variant="caption">{item.updated}</Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TextField
                label={t('item:itemFamily')}
                name="itemFamilyName"
                value={itemData.itemFamilyName}
                onChange={handleFieldChange}
                disabled={fieldIsDisabled('itemFamilyName')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t('item:ean')}
                name="eanCode"
                value={itemData.eanCode}
                onChange={handleFieldChange}
                disabled={fieldIsDisabled('eanCode')}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label={t('item:itemLifeCycle')}
                name="ean"
                value={itemData.itemLifeCycle}
                onChange={handleFieldChange}
                disabled={fieldIsDisabled('itemLifeCycle')}
              />
            </Grid>
          </Grid>

          <Box my={5}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:markets')}</Typography></Box>
            <DeleteList
              items={filterCountryExclusions()}
              onDelete={(item) => handleDeleteListItem('excludeMarkets', item)}
              disabled={fieldIsDisabled('excludeMarkets')}
            />
          </Box>

          <Box my={3}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:attributes')}</Typography></Box>
            <DeleteList
              items={filterAttributeExclusions('itemFamilyAttributes', 'excludeAttributes', 'attributes')}
              onDelete={(item) => handleDeleteListItem('excludeAttributes', item)}
              disabled={fieldIsDisabled('excludeAttributes')}
            />
          </Box>

          <Box my={5}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('item:certificateInfo')}</Typography></Box>
            <Grid container spacing={3}>

              <Grid item xs={6}>
                <TextField
                  label={t('item:loadCapacityComment')}
                  name="loadCapacityComment"
                  value={itemData.loadCapacityComment || ''}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('loadCapacityComment')}
                  multiline={true}
                  maxRows={4}
                  helperText={t('item:loadCapacityCommentHelp')}
                />
              </Grid>
            </Grid>
          </Box>

          <Box my={5}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('item:technicalInfo')}</Typography></Box>

            {
              isTBR
                ?
                <Grid container spacing={3}>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:rimRecommendedSize')}
                      name="rimRecommendedSizeCode"
                      value={itemData.rimRecommendedSizeCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('rimRecommendedSizeCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:rimPermittedSizes')}
                      name="rimPermittedSizes"
                      value={itemData.rimPermittedSizes}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('rimPermittedSizes')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:measuredWidth')} (mm)`}
                      name="measuredWidthMM"
                      value={itemData.measuredWidthMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('measuredWidthMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:measuredWidth')} (in)`}
                      name="measuredWidthIN"
                      value={itemData.measuredWidthIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('measuredWidthIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:measuredDiameter')} (mm)`}
                      name="measuredDiameterMM"
                      value={itemData.measuredDiameterMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('measuredDiameterMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:measuredDiameter')} (in)`}
                      name="measuredDiameterIN"
                      value={itemData.measuredDiameterIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('measuredDiameterIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:loadedStaticRadius')} (mm)`}
                      name="loadedStaticRadiusMM"
                      value={itemData.loadedStaticRadiusMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('loadedStaticRadiusMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:loadedStaticRadius')} (in)`}
                      name="loadedStaticRadiusIN"
                      value={itemData.loadedStaticRadiusIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('loadedStaticRadiusIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:rollingCircumference')} (mm)`}
                      name="rollingCircumferenceMM"
                      value={itemData.rollingCircumferenceMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('rollingCircumferenceMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:rollingCircumference')} (in)`}
                      name="rollingCircumferenceIN"
                      value={itemData.rollingCircumferenceIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('rollingCircumferenceIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:minDualSpacing')} (mm)`}
                      name="minDualSpacingMM"
                      value={itemData.minDualSpacingMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('minDualSpacingMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:minDualSpacing')} (in)`}
                      name="minDualSpacingIN"
                      value={itemData.minDualSpacingIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('minDualSpacingIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:actualTotalTreadDepth')} (mm)`}
                      name="actualTotalTreadDepthMM"
                      value={itemData.actualTotalTreadDepthMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('actualTotalTreadDepthMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:actualTotalTreadDepth')} (in)`}
                      name="actualTotalTreadDepthIN"
                      value={itemData.actualTotalTreadDepthIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('actualTotalTreadDepthIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:numberOfStudsAllowed')}
                      name="numberOfStudsAllowed"
                      value={itemData.numberOfStudsAllowed}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('numberOfStudsAllowed')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:recommendedStud')}
                      name="recommendedStud"
                      value={itemData.recommendedStud}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('recommendedStud')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BooleanField
                      label={t('item:studdable')}
                      name="studdable"
                      value={itemData.studdable}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('studdable')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BooleanField
                      label={t('item:studMarking')}
                      name="studMarkingCode"
                      value={itemData.studMarkingCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('studMarkingCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BooleanField
                      label={t('item:mudSnowMarking')}
                      name="mudSnowMarkingCode"
                      value={itemData.mudSnowMarkingCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('mudSnowMarkingCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BooleanField
                      label={t('item:PorMarking')}
                      name="pORMarkingCode"
                      value={itemData.pORMarkingCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('pORMarkingCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BooleanField
                      label={t('item:regroovable')}
                      name="regroovableCode"
                      value={itemData.regroovableCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('regroovableCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:typeApproval')}
                      name="typeApproval"
                      value={itemData.typeApproval}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('typeApproval')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:speedRadiusIndex')}
                      name="speedRadiusIndex"
                      value={itemData.speedRadiusIndex}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('speedRadiusIndex')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:applicationCode')}
                      name="applicationCode"
                      value={itemData.applicationCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('applicationCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:primaryUse')}
                      name="primaryUseCode"
                      value={itemData.primaryUseCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('primaryUseCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:secondaryUse')}
                      name="secondaryseCode"
                      value={itemData.secondaryseCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('secondaryseCode')}
                    />
                  </Grid>

                </Grid>
                :
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <TextField
                      label={t('item:rimRecommendedSize')}
                      name="rimRecommendedSizeCode"
                      value={itemData.rimRecommendedSizeCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('rimRecommendedSizeCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:rimPermittedSizes')}
                      name="rimPermittedSizes"
                      value={itemData.rimPermittedSizes}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('rimPermittedSizes')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:measuredWidth')} (mm)`}
                      name="measuredWidthMM"
                      value={itemData.measuredWidthMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('measuredWidthMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:measuredWidth')} (in)`}
                      name="measuredWidthIN"
                      value={itemData.measuredWidthIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('measuredWidthIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:measuredDiameter')} (mm)`}
                      name="measuredDiameterMM"
                      value={itemData.measuredDiameterMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('measuredDiameterMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:measuredDiameter')} (in)`}
                      name="measuredDiameterIN"
                      value={itemData.measuredDiameterIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('measuredDiameterIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:loadedStaticRadius')} (mm)`}
                      name="loadedStaticRadiusMM"
                      value={itemData.loadedStaticRadiusMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('loadedStaticRadiusMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:loadedStaticRadius')} (in)`}
                      name="loadedStaticRadiusIN"
                      value={itemData.loadedStaticRadiusIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('loadedStaticRadiusIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:loadedStaticRadius')} 2 (mm)`}
                      name="loadedStaticRadius2MM"
                      value={itemData.loadedStaticRadius2MM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('loadedStaticRadius2MM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:loadedStaticRadius')} 2 (in)`}
                      name="loadedStaticRadius2IN"
                      value={itemData.loadedStaticRadius2IN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('loadedStaticRadius2IN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:rollingCircumference')} (mm)`}
                      name="rollingCircumferenceMM"
                      value={itemData.rollingCircumferenceMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('rollingCircumferenceMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:rollingCircumference')} (in)`}
                      name="rollingCircumferenceIN"
                      value={itemData.rollingCircumferenceIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('rollingCircumferenceIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:rollingCircumference')} 2 (mm)`}
                      name="rollingCircumference2MM"
                      value={itemData.rollingCircumference2MM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('rollingCircumference2MM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:rollingCircumference')} 2 (in)`}
                      name="rollingCircumference2IN"
                      value={itemData.rollingCircumference2IN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('rollingCircumference2IN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:minDualSpacing')} (mm)`}
                      name="minDualSpacingMM"
                      value={itemData.minDualSpacingMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('minDualSpacingMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:minDualSpacing')} (in)`}
                      name="minDualSpacingIN"
                      value={itemData.minDualSpacingIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('minDualSpacingIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:actualTotalTreadDepth')} (mm)`}
                      name="actualTotalTreadDepthMM"
                      value={itemData.actualTotalTreadDepthMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('actualTotalTreadDepthMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:actualTotalTreadDepth')} (in)`}
                      name="actualTotalTreadDepthIN"
                      value={itemData.actualTotalTreadDepthIN}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('actualTotalTreadDepthIN')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:numberOfStudsAllowed')}
                      name="numberOfStudsAllowed"
                      value={itemData.numberOfStudsAllowed}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('numberOfStudsAllowed')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:recommendedStud')}
                      name="recommendedStud"
                      value={itemData.recommendedStud}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('recommendedStud')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BooleanField
                      label={t('item:studdable')}
                      name="studdable"
                      value={itemData.studdable}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('studdable')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BooleanField
                      label={t('item:studMarking')}
                      name="studMarkingCode"
                      value={itemData.studMarkingCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('studMarkingCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BooleanField
                      label={t('item:mudSnowMarking')}
                      name="mudSnowMarkingCode"
                      value={itemData.mudSnowMarkingCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('mudSnowMarkingCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BooleanField
                      label={t('item:tractionMarking')}
                      name="tractionMarkingCode"
                      value={itemData.tractionMarkingCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('tractionMarkingCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BooleanField
                      label={t('item:PorMarking')}
                      name="pORMarkingCode"
                      value={itemData.pORMarkingCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('pORMarkingCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <BooleanField
                      label={t('item:regroovable')}
                      name="regroovableCode"
                      value={itemData.regroovableCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('regroovableCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:carcassMaterial')}
                      name="carcassMaterialCode"
                      value={itemData.carcassMaterialCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('carcassMaterialCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:beltsBreakers')}
                      name="beltsBreakersCode"
                      value={itemData.beltsBreakersCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('beltsBreakersCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:footPrintAreaHardSurface')} (mm²)`}
                      name="footPrintAreaHardSurfaceMM2"
                      value={itemData.footPrintAreaHardSurfaceMM2}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('footPrintAreaHardSurfaceMM2')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:maxDiameterInService')} (mm)`}
                      name="maxDiameterInServiceMM"
                      value={itemData.maxDiameterInServiceMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('maxDiameterInServiceMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={`${t('item:maxWidthInService')} (mm)`}
                      name="maxWidthInServiceMM"
                      value={itemData.maxWidthInServiceMM}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('maxWidthInServiceMM')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:traTiretype')}
                      name="tRATiretypeCode"
                      value={itemData.tRATiretypeCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('tRATiretypeCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:typeApproval')}
                      name="typeApproval"
                      value={itemData.typeApproval}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('typeApproval')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:speedRadiusIndex')}
                      name="speedRadiusIndex"
                      value={itemData.speedRadiusIndex}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('speedRadiusIndex')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:prStarRating')}
                      name="prStarRatingName"
                      value={itemData.prStarRatingName}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('prStarRatingName')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:tubeProduct')}
                      name="tubeProductCode"
                      value={itemData.tubeProductCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('tubeProductCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:applicationCode')}
                      name="applicationCode"
                      value={itemData.applicationCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('applicationCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:flapProduct')}
                      name="flapProductCode"
                      value={itemData.flapProductCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('flapProductCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:valveTubeType')}
                      name="valveTubeTypeCode"
                      value={itemData.valveTubeTypeCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('valveTubeTypeCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:valveTubeless')}
                      name="valveTubelessCode"
                      value={itemData.valveTubelessCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('valveTubelessCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:primaryUse')}
                      name="primaryUseCode"
                      value={itemData.primaryUseCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('primaryUseCode')}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label={t('item:secondaryUse')}
                      name="secondaryseCode"
                      value={itemData.secondaryseCode}
                      onChange={handleFieldChange}
                      disabled={fieldIsDisabled('secondaryseCode')}
                    />
                  </Grid>
                </Grid>
            }
          </Box>

          { isTBR ? null : <Box my={5}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('item:waterAndSaltBallasting')}</Typography></Box>
            <Grid container spacing={3}>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:internalVolume')} (l)`}
                  name="internalVolumeL"
                  value={itemData.internalVolumeL}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('internalVolumeL')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:internalVolume')} (gal)`}
                  name="internalVolumeGal"
                  value={itemData.internalVolumeGal}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('internalVolumeGal')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:waterFilling')} (75 l)`}
                  name="waterFilling75L"
                  value={itemData.waterFilling75L}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('waterFilling75L')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:waterFilling')} (75 gal)`}
                  name="waterFilling75Gal"
                  value={itemData.waterFilling75Gal}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('waterFilling75Gal')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:water')} (l)`}
                  name="waterL"
                  value={itemData.waterL}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('waterL')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:water')} (gal)`}
                  name="waterGal"
                  value={itemData.waterGal}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('waterGal')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:salt')} (CaCl Kg)`}
                  name="saltCaCl2Kg"
                  value={itemData.saltCaCl2Kg}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('saltCaCl2Kg')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:salt')} (CaCl lb)`}
                  name="saltCaCl2Lbs"
                  value={itemData.saltCaCl2Lbs}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('saltCaCl2Lbs')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:totalWeightIncrease')} (Kg)`}
                  name="totalWeightIncreaseKg"
                  value={itemData.totalWeightIncreaseKg}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('totalWeightIncreaseKg')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={`${t('item:totalWeightIncrease')} (lb)`}
                  name="totalWeightIncreaseLbs"
                  value={itemData.totalWeightIncreaseLbs}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('totalWeightIncreaseLbs')}
                />
              </Grid>

            </Grid>
          </Box> }

          <Box my={5}>
            <Box mb={3}><Typography variant="h6" component="h2">{t('item:euLabelInfo')}</Typography></Box>

            <Grid container spacing={3}>

              <Grid item xs={6}>
                <TextField
                  label={t('item:euDirectiveNumber')}
                  name="eUDirectiveNumberCode"
                  value={itemData.eUDirectiveNumberCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('eUDirectiveNumberCode')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={t('item:tyreClass')}
                  name="tyreClass"
                  value={itemData.tyreClass}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('tyreClass')}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={t('item:loadIndexSpeedSymbol')}
                  value={loadIndex}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={t('item:additionalMarking')}
                  value={loadIndex2}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label={t('item:rollingResistance')}
                  name="rollingResistanceCode"
                  value={itemData.rollingResistanceCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('rollingResistanceCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('item:wetGrip')}
                  name="wetGripCode"
                  value={itemData.wetGripCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('wetGripCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <BooleanField
                  label={t('item:snowGrip')}
                  name="snowGripCode"
                  value={itemData.snowGripCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('snowGripCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <BooleanField
                  label={t('item:iceGrip')}
                  name="iceGripCode"
                  value={itemData.iceGripCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('iceGripCode')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('item:noisePerformance')}
                  name="noisePerformance"
                  value={itemData.noisePerformance}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('noisePerformance')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('item:noiseClass')}
                  name="noiseClassCode"
                  value={itemData.noiseClassCode}
                  onChange={handleFieldChange}
                  disabled={fieldIsDisabled('noiseClassCode')}
                />
              </Grid>


            </Grid>
          </Box>

        </Grid>
        <Grid item xs={12} md={4}>
          <MainImage src={itemData.imageMain?.medium || itemData.itemFamilyImage?.medium} />
          <FilePicker
            fieldName={t('item:overrideImageMain')}
            fieldValue={itemData.imageMain}
            setFieldValue={(file) => handleChange('imageMain', file)}
            disabled={fieldIsDisabled('imageMain')}
            rootFolderId={defaultStudioFolder}
          />

          <FilePicker viewOnly={true} fieldName={t('item:imageEuLabel')} fieldValue={itemData.imageEuLabel} />
          <FilePicker viewOnly={true} fieldName={t('item:certificate')} fieldValue={itemData.certificate} />
          <FilePicker viewOnly={true} fieldName={`${t('item:certificate')} (${t('item:imperial')})`} fieldValue={itemData.certificateImperial} />
        </Grid>
      </Grid>

      {hasUnsavedChanges ? <ActionBar cancelChanges={cancelChanges} saveChanges={saveChanges} disabled={saveStatus === 'loading'} /> : null}
    </>
  );


};

ItemPagePC24.propTypes = {
  canEdit: PropTypes.bool
};

export default ItemPagePC24;
